import React, { useState, useMemo, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    IconLayoutGrid, IconPencil, IconCopy, IconTrash, IconPlayerPlay,
    IconFileOff, IconEye, IconDeviceFloppy,
    IconX, IconUser, IconClock, IconCircleCheck, IconDownload,
} from '@tabler/icons-react'
import {
    getSwmsStore,
    getSwmsStoreItem,
    downloadSwmsStorePdf,
    createSwmsDraft,
    updateSwmsDraft,
    deleteSwmsDraft,
    copySwms,
    publishSwms,
    retireSwms,
} from 'services/swmsStore.svc'
import { getSwmsTemplates, getSwmsTemplateUrl } from 'services/swmsTemplates.svc'
import { getCompanyById } from 'services/clients.svc'
import ConfirmModal from './ConfirmModal'

// ── Status badge ──────────────────────────────────────────────────────────────

const BADGE_CLASS = {
    template: 'sb-template',
    draft: 'sb-draft',
    published: 'sb-published',
    retired: 'sb-retired',
}
const BADGE_LABEL = { template: 'Template', draft: 'Draft', published: 'Published', retired: 'Retired' }

function StatusBadge({ type }) {
    return (
        <span className={`sm-status-badge ${BADGE_CLASS[type] || 'sb-draft'}`}>
            {BADGE_LABEL[type] || type}
        </span>
    )
}

// ── Card icon ─────────────────────────────────────────────────────────────────

const ICON_CLASS = { templates: 'icon-tpl', draft: 'icon-draft', published: 'icon-pub', retired: 'icon-arch' }

const CARD_ICON_MAP = {
    templates: <i className="ti ti-template" />,
    draft:     <IconPencil   size={18} stroke={1.5} />,
    published: <IconCircleCheck size={18} stroke={1.5} />,
    retired:   <IconFileOff  size={18} stroke={1.5} />,
}

function CardDocIcon({ type }) {
    return (
        <div className={`sm-card-icon ${ICON_CLASS[type] || 'icon-draft'}`}>
            {CARD_ICON_MAP[type] || <IconPencil size={18} stroke={1.5} />}
        </div>
    )
}

// ── SWMS card ─────────────────────────────────────────────────────────────────

function SwmsCard({ record, type, onEdit, onCopy, onDelete, onPublish, onRetire, onView, onUseTemplate }) {
    const isDraft = type === 'draft'
    const isPublished = type === 'published'
    const isRetired = type === 'retired'
    const isTemplate = type === 'templates'

    const fullName = (details) => details
        ? `${details.firstName} ${details.surname}`
        : null

    const createdByName = fullName(record.createdByDetails)
    const publishedByName = fullName(record.publishedByDetails)
    const retiredByName = fullName(record.retiredByDetails)

    const actionByName = isRetired ? retiredByName : isPublished ? publishedByName : createdByName
    const actionByLabel = isRetired ? 'Retired by' : isPublished ? 'Published by' : 'Created by'
    const actionDate = isRetired
        ? record.retiredAt
        : isPublished
        ? record.publishedAt
        : record.createdAt

    return (
        <div className={`sm-card${isTemplate ? ' template-card' : ''}`}>
            <CardDocIcon type={isTemplate ? 'templates' : type} />
            <p className="sm-card-name">{record.name || record.title}</p>
            {!isTemplate && (
                <>
                    <p className="sm-card-date">
                        <IconClock size={11} stroke={1.5} />
                        {actionDate ? new Date(actionDate).toLocaleString() : ''}
                    </p>
                    {actionByName && (
                        <p className="sm-card-created">
                            <IconUser size={11} stroke={1.5} style={{ marginRight: 3 }} />
                            {actionByLabel}: {actionByName}
                        </p>
                    )}
                </>
            )}
            <StatusBadge type={isTemplate ? 'template' : type} />
            <div className="sm-card-actions">
                {isTemplate && (
                    <button className="act-btn act-tpl" onClick={() => onUseTemplate(record)}>
                        <i className="ti ti-plus" />
                        Use template
                    </button>
                )}
                {isDraft && <>
                    <button className="act-btn act-edit" onClick={() => onEdit(record)}>
                        <IconPencil size={12} stroke={1.5} /> Edit
                    </button>
                    <button className="act-btn act-copy" onClick={() => onCopy(record)}>
                        <IconCopy size={12} stroke={1.5} /> Copy
                    </button>
                    <button className="act-btn act-pub" onClick={() => onPublish(record)}>
                        <IconPlayerPlay size={12} stroke={1.5} /> Publish
                    </button>
                    <button className="act-btn act-del" onClick={() => onDelete(record)}>
                        <IconTrash size={12} stroke={1.5} /> Delete
                    </button>
                </>}
                {isPublished && <>
                    <button className="act-btn act-view" onClick={() => onView(record)}>
                        <IconEye size={12} stroke={1.5} /> View
                    </button>
                    <button className="act-btn act-newdraft" onClick={() => onCopy(record)}>
                        <IconCopy size={12} stroke={1.5} /> New Draft
                    </button>
                    <button className="act-btn act-retire" onClick={() => onRetire(record)}>
                        <IconFileOff size={12} stroke={1.5} /> Retire
                    </button>
                </>}
                {isRetired && (
                    <button className="act-btn act-view" onClick={() => onView(record)}>
                        <IconEye size={12} stroke={1.5} /> View
                    </button>
                )}
            </div>
        </div>
    )
}

// ── Section band ──────────────────────────────────────────────────────────────

const BAND_CLASS = { templates: 's-tpl', draft: 's-draft', published: 's-pub', retired: 's-arch' }

const SECTION_ICON = {
    templates: <IconLayoutGrid size={16} stroke={1.5} />,
    draft:     <IconPencil    size={16} stroke={1.5} />,
    published: <IconCircleCheck size={16} stroke={1.5} />,
    retired:   <IconFileOff   size={16} stroke={1.5} />,
}

const SECTION_COUNT_LABEL = {
    templates: 'templates',
    draft: 'drafts',
    published: 'published',
    retired: 'retired',
}

const SECTION_HELP = {
    templates: <><b>Templates</b> are pre-built starting points provided by the system. Pick one to create a new draft — your content, not the template, becomes your SWMS.</>,
    draft: <><b>Drafts</b> are works in progress. They can be edited, copied or deleted freely. A draft must be published before it can be assigned to a site or signed by workers.</>,
    published: <><b>Published SWMS</b> are locked, permanent records. They cannot be edited or deleted — this protects your compliance record. To make changes, create a new draft from this document instead.</>,
    retired: <><b>Retired SWMS</b> are documents no longer in active use. They are read-only and kept permanently for your compliance history. Retiring a SWMS does not affect existing assignments — those remain intact as independent records.</>,
}

function SectionBand({ sectionKey, title, items, children, collapsed, onToggle }) {
    const bandCls = BAND_CLASS[sectionKey] || 's-draft'
    const icon = SECTION_ICON[sectionKey]
    const helpText = SECTION_HELP[sectionKey]
    const countLabel = SECTION_COUNT_LABEL[sectionKey] || sectionKey
    return (
        <div className={`sm-section-band ${bandCls}`}>
            <div className="sm-section-header" onClick={onToggle}>
                <div className="sm-section-title">
                    {icon}
                    {title}
                </div>
                <div className="sm-section-right">
                    <span className="sm-section-count">{items} {countLabel}</span>
                    {helpText && (
                        <div className="sm-help-wrap">
                            <div className="sm-help-btn">?</div>
                            <div className="sm-help-tooltip">{helpText}</div>
                        </div>
                    )}
                    <button className={`sm-collapse-btn${collapsed ? ' collapsed' : ''}`} onClick={(e) => { e.stopPropagation(); onToggle() }}>
                        <i className="ti ti-chevron-down" />
                    </button>
                </div>
            </div>
            {!collapsed && (
                <div className="sm-section-cards">
                    {children}
                </div>
            )}
        </div>
    )
}

// ── Confirm modal ─────────────────────────────────────────────────────────────

// ── Draft editor overlay ──────────────────────────────────────────────────────
// NOTE: We use a `key` prop at the call-site so React unmounts/remounts this
// component every time the editor opens with a new record — guaranteeing that
// local state is always initialised from the record, and Discard truly discards.
//
// Editing model: the iframe renders the template HTML as-is (no designMode).
// Structural content (headings, labels, divs) is intentionally read-only —
// builders fill in form fields only. On Save, field values are snapshotted by
// position into data-swms-state on <body>, then outerHTML is persisted so the
// exact state is restored when the draft is re-opened.

// ── Field state helpers ───────────────────────────────────────────────────────
// ID-keyed snapshot/restore (Approach 2).
// Every template field has a stable `swms-f-NNN` id injected at upload time.
// Fields without an id are ignored — they cannot be reliably round-tripped.

/**
 * Capture current field values from the live iframe DOM.
 * Returns a plain object { [fieldId]: value | boolean }.
 */
const snapshotFields = (doc) => {
    const result = {}
    doc.querySelectorAll('input, textarea, select').forEach((el) => {
        if (!el.id) return // skip anonymous fields — no stable key
        if (el.type === 'checkbox' || el.type === 'radio') {
            result[el.id] = el.checked
        } else {
            result[el.id] = el.value
        }
    })
    return result
}

/**
 * Restore field values into the live iframe DOM from an ID-keyed object.
 * Only elements with a matching id are updated.
 */
const restoreFields = (doc, fieldValues) => {
    if (!fieldValues || typeof fieldValues !== 'object') return
    Object.entries(fieldValues).forEach(([id, val]) => {
        const el = doc.getElementById(id)
        if (!el) return
        if (el.type === 'checkbox' || el.type === 'radio') {
            el.checked = Boolean(val)
        } else {
            el.value = val ?? ''
        }
    })
}

/**
 * Freeze all form fields in the viewer DOM — replace inputs/textareas/selects
 * with static auto-sizing divs.  Applied client-side in the read-only viewer
 * so text never clips inside a disabled input box.
 *
 * fieldValues (ID-keyed) takes priority; element's own current value is the
 * fallback so partial fieldValues maps still produce correct output.
 */
const freezeFieldsInDom = (doc, fieldValues = {}) => {
    const STYLE = 'font-family:Georgia,serif;font-size:10.5pt;font-weight:normal;color:#1a3a5c;' +
        'padding:1px 4px;word-break:break-word;white-space:pre-wrap;display:block;min-height:1em;'
    const fv = fieldValues && typeof fieldValues === 'object' ? fieldValues : {}

    doc.querySelectorAll('input, textarea, select').forEach((el) => {
        const id = el.id || ''
        const div = doc.createElement('div')
        if (id) div.id = id
        div.setAttribute('style', STYLE)

        if (el.type === 'checkbox' || el.type === 'radio') {
            const checked = id && fv[id] !== undefined ? Boolean(fv[id]) : el.checked
            div.style.textAlign = 'center'
            div.textContent = checked ? '✓' : '□'
        } else {
            const value = id && fv[id] !== undefined ? fv[id] : (el.value || el.textContent || '')
            div.style.whiteSpace = 'pre-wrap'
            div.textContent = value
        }

        el.parentNode?.replaceChild(div, el)
    })

    // Hide all <button> elements — editing controls (delete row, add row, etc.)
    // have no meaning in a read-only view.
    doc.querySelectorAll('button').forEach((btn) => {
        btn.style.display = 'none'
    })
}

function DraftEditor({ initTitle, initContent, initFieldValues, onSave, onClose, isSaving }) {
    const [title, setTitle] = useState(initTitle || '')
    const iframeRef = useRef(null)

    // Strip legacy restore script and data-swms-state attribute from saved HTML
    // so they are not duplicated on re-open or re-save.
    const editorHtml = useMemo(() => {
        if (!initContent) return ''
        return initContent
            .replace(/<script>\(function\(\)\{var s=JSON\.parse[\s\S]*?\}\)\(\)<\/script>/g, '')
            .replace(/\s*data-swms-state="[^"]*"/g, '')
            .replace(/\s*data-swms-state='[^']*'/g, '')
    }, [initContent])

    // Restore field values from the DB fieldValues map once the iframe has loaded.
    // No designMode — structural content (headings, labels, divs) is read-only.
    // Form fields (input, textarea, select, checkbox) remain interactive natively.
    const handleIframeLoad = () => {
        try {
            const doc = iframeRef.current?.contentDocument
            if (!doc) return

            // Restore from DB fieldValues (ID-keyed map)
            if (initFieldValues && Object.keys(initFieldValues).length > 0) {
                restoreFields(doc, initFieldValues)
            }

            // Auto-expand textareas to fit restored content (programmatic value
            // set does not fire input events, so we handle it explicitly here)
            doc.querySelectorAll('textarea').forEach((ta) => {
                ta.style.height = 'auto'
                ta.style.height = ta.scrollHeight + 'px'
            })

            // Wire up live auto-expand for user typing
            doc.addEventListener('input', (e) => {
                if (e.target.tagName === 'TEXTAREA') {
                    e.target.style.height = 'auto'
                    e.target.style.height = e.target.scrollHeight + 'px'
                }
            })

            // Activate editor-only UI (delete buttons, add-row button)
            doc.body.classList.add('swms-editor-active')

            // Add task row — assign unique IDs so snapshot/restore is always ID-based
            doc.getElementById('addTaskRow')?.addEventListener('click', () => {
                const tbody = doc.querySelector('#tasksTable tbody')
                if (!tbody) return
                const rowIdx = `dyn-${Date.now()}`
                const tr = doc.createElement('tr')
                tr.className = 'tasks-input-row'
                tr.innerHTML =
                    `<td><textarea id="task-${rowIdx}-c0" style="overflow:hidden;resize:none"></textarea></td>` +
                    `<td><textarea id="task-${rowIdx}-c1" style="overflow:hidden;resize:none"></textarea></td>` +
                    `<td><button class="btn-del-row" type="button" title="Delete row">✕</button>` +
                    `<textarea id="task-${rowIdx}-c2" style="overflow:hidden;resize:none"></textarea></td>`
                tbody.appendChild(tr)
                tr.querySelector('.btn-del-row').addEventListener('click', () => tr.remove())
            })

            // Delete task row — wire existing rows
            doc.querySelectorAll('.btn-del-row').forEach((btn) => {
                btn.addEventListener('click', () => btn.closest('tr')?.remove())
            })
        } catch {
            // cross-origin guard – should never fire with srcDoc
        }
    }

    // Capture live DOM: snapshot fields by ID into fieldValues, clean outerHTML.
    // fieldValues is sent to the API separately; the HTML no longer embeds state.
    const getLiveSnapshot = () => {
        try {
            const doc = iframeRef.current?.contentDocument
            if (doc) {
                const fieldValues = snapshotFields(doc)
                // Clean any legacy state attributes from the HTML
                let html = doc.documentElement.outerHTML
                html = html.replace(/<script>\(function\(\)\{var s=JSON\.parse[\s\S]*?\}\)\(\)<\/script>/g, '')
                html = html.replace(/\s*data-swms-state="[^"]*"/g, '')
                html = html.replace(/\s*data-swms-state='[^']*'/g, '')
                console.debug('[DraftEditor] getLiveSnapshot — fieldValues:', fieldValues)
                return { content: html, fieldValues }
            }
        } catch (err) {
            console.warn('[DraftEditor] getLiveSnapshot — failed to read iframe DOM:', err)
        }
        return { content: initContent || '', fieldValues: initFieldValues || {} }
    }

    return (
        <div className="sm-editor-overlay open" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
            <div className="sm-editor-panel">
                <div className="sm-editor-topbar">
                    <div className="sm-editor-topbar-left">
                        <input
                            className="sm-editor-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Untitled SWMS"
                        />
                    </div>
                    <div className="sm-editor-actions">
                        <button className="sm-confirm-cancel" onClick={onClose}>Discard</button>
                        <button
                            className="sm-confirm-ok publish"
                            onClick={() => { const snap = getLiveSnapshot(); onSave({ title, content: snap.content, fieldValues: snap.fieldValues }) }}
                            disabled={isSaving || !title.trim()}
                        >
                            <IconDeviceFloppy size={14} stroke={1.5} />
                            {isSaving ? 'Saving…' : 'Save draft'}
                        </button>
                    </div>
                </div>
                <div className="sm-editor-body">
                    {/*
                      * No sandbox: template scripts (e.g. "Add row" buttons) must execute
                      * during editing. allow-scripts + allow-same-origin defeats sandboxing
                      * anyway, and the content is trusted (SuperUser-authored templates
                      * edited by authenticated CompanyAdmins). Removing sandbox is the
                      * honest equivalent with no false sense of security.
                      */}
                    <div className="sm-editor-preview">
                        <iframe
                            ref={iframeRef}
                            srcDoc={editorHtml}
                            title="SWMS Editor"
                            sandbox="allow-same-origin allow-scripts"
                            onLoad={handleIframeLoad}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

// ── Read-only viewer overlay ──────────────────────────────────────────────────

function ViewerOverlay({ open, record, onClose }) {
    const iframeRef = useRef(null)
    const [downloading, setDownloading] = useState(false)

    const handleDownloadPdf = async () => {
        if (!record?._id || downloading) return
        setDownloading(true)
        try {
            const response = await downloadSwmsStorePdf(record._id)
            const blob = await response.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${(record.title || 'swms').replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_')}.pdf`
            a.click()
            URL.revokeObjectURL(url)
        } catch {
            // silently ignore — user can retry
        } finally {
            setDownloading(false)
        }
    }

    const handleIframeLoad = useCallback(() => {
        try {
            const doc = iframeRef.current?.contentDocument
            if (!doc) return
            // Freeze all form fields to static auto-sizing divs using the
            // ID-keyed fieldValues from the DB (no disabled-input clipping).
            freezeFieldsInDom(doc, record?.fieldValues || {})
        } catch {
            // cross-origin guard
        }
    }, [record?._id])

    if (!open || !record) return null
    const badgeClass = record.status === 'retired' ? 'vt-arch' : 'vt-pub'
    const badgeLabel = record.status === 'retired' ? 'Retired' : 'Published'
    return (
        <div className="sm-view-overlay open" onClick={onClose}>
            <div className="sm-view-shell" onClick={(e) => e.stopPropagation()}>
                <div className="sm-view-toolbar">
                    <div className="sm-view-toolbar-left">
                        <span className={`sm-view-badge ${badgeClass}`}>{badgeLabel}</span>
                    </div>
                    <span className="sm-view-title">{record.title}</span>
                    <div className="sm-view-toolbar-right">
                        <button className="sm-vt-btn sm-vt-btn-download" onClick={handleDownloadPdf} disabled={downloading}>
                            <IconDownload size={14} stroke={1.5} /> {downloading ? 'Generating…' : 'Download PDF'}
                        </button>
                        <button className="sm-vt-btn sm-vt-btn-close" onClick={onClose}>
                            <IconX size={14} stroke={1.5} /> Close
                        </button>
                    </div>
                </div>
                <div className="sm-view-scroll">
                    <iframe
                        ref={iframeRef}
                        srcDoc={record.content || ''}
                        title="SWMS View"
                        sandbox="allow-same-origin"
                        onLoad={handleIframeLoad}
                    />
                </div>
            </div>
        </div>
    )
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, show, isError }) {
    return (
        <div className={`sm-toast${show ? ' show' : ''}${isError ? ' sm-toast-error' : ''}`}>
            {message}
        </div>
    )
}

// ── Main SwmsStore component ──────────────────────────────────────────────────

export default function SwmsStore() {
    const { companyId } = useParams()
    const queryClient = useQueryClient()

    const [collapsed, setCollapsed] = useState({ templates: false, draft: false, published: false, retired: true })
    const [tradeFilter, setTradeFilter] = useState(null)
    const [editor, setEditor] = useState({ open: false, record: null })
    const [viewer, setViewer] = useState({ open: false, record: null })
    const [confirm, setConfirm] = useState(null)
    const [toast, setToast] = useState({ show: false, message: '', isError: false })
    const toastTimer = useRef(null)

    const showToast = (message, isError = false) => {
        clearTimeout(toastTimer.current)
        setToast({ show: true, message, isError })
        toastTimer.current = setTimeout(() => setToast({ show: false, message: '', isError: false }), 4000)
    }

    // ── Queries ───────────────────────────────────────────────────────────────
    const { data: allRecords = [], isLoading: loadingStore } = useQuery(
        ['swmsStore', companyId],
        () => getSwmsStore()
    )
    const { data: templates = [], isLoading: loadingTemplates } = useQuery(
        ['swmsTemplates'],
        getSwmsTemplates
    )

    // ── Derived data ──────────────────────────────────────────────────────────
    const applyFilter = (items) => {
        if (!tradeFilter) return items
        return items.filter((r) => !r.trades?.length || r.trades.includes(tradeFilter))
    }
    const drafts = useMemo(() => applyFilter(allRecords.filter((r) => r.status === 'draft')), [allRecords, tradeFilter])
    const published = useMemo(() => applyFilter(allRecords.filter((r) => r.status === 'published')), [allRecords, tradeFilter])
    const retired = useMemo(() => applyFilter(allRecords.filter((r) => r.status === 'retired')), [allRecords, tradeFilter])
    const activeTemplates = useMemo(() => templates.filter((t) => t.isActive), [templates])

    const availableTrades = useMemo(() => {
        const trades = new Set(allRecords.flatMap((r) => r.trades || []))
        return [...trades]
    }, [allRecords])

    // ── Mutations ─────────────────────────────────────────────────────────────
    const invalidate = () => queryClient.invalidateQueries(['swmsStore', companyId])

    const createMutation = useMutation(createSwmsDraft, {
        onSuccess: () => { invalidate(); setEditor({ open: false, record: null }); showToast('Draft created') },
        onError: (e) => showToast(e.message || 'Failed to create draft', true),
    })
    const updateMutation = useMutation(({ id, data }) => updateSwmsDraft(id, data), {
        onSuccess: () => { invalidate(); setEditor({ open: false, record: null }); showToast('Draft saved') },
        onError: (e) => showToast(e.message || 'Failed to save draft', true),
    })
    const deleteMutation = useMutation(deleteSwmsDraft, {
        onSuccess: () => { invalidate(); showToast('Draft deleted') },
        onError: (e) => showToast(e.message || 'Failed to delete draft', true),
    })
    const copyMutation = useMutation(copySwms, {
        onSuccess: () => { invalidate(); showToast('Copy created as draft') },
        onError: (e) => showToast(e.message || 'Failed to copy', true),
    })
    const publishMutation = useMutation(publishSwms, {
        onSuccess: () => { invalidate(); showToast('SWMS published') },
        onError: (e) => showToast(e.message || 'Failed to publish', true),
    })
    const retireMutation = useMutation(retireSwms, {
        onSuccess: () => { invalidate(); showToast('SWMS retired') },
        onError: (e) => showToast(e.message || 'Failed to retire', true),
    })

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleUseTemplate = async (template) => {
        try {
            const [{ url }, company] = await Promise.all([
                getSwmsTemplateUrl(template._id),
                getCompanyById(companyId),
            ])
            let html = await fetch(url).then((r) => r.text())
            const name = company?.name || ''
            // Auto-populate readonly company fields — strip any existing value first
            html = html
                .replace(/(<input[^>]*id="companyName"[^>]*?)\s*(?:value="[^"]*")?\s*>/,        `$1 value="${name}">`)
                .replace(/(<input[^>]*id="pcbu"[^>]*?)\s*(?:value="[^"]*")?\s*>/,               `$1 value="${name}">`)
                .replace(/(<input[^>]*id="principalContractor"[^>]*?)\s*(?:value="[^"]*")?\s*>/, `$1 value="${name}">`)
            setEditor({ open: true, record: { title: template.name, content: html, fieldValues: {}, _id: null } })
        } catch {
            showToast('Failed to load template')
        }
    }

    const handleEdit = async (rec) => {
        try {
            // Fetch the full record (including content + fieldValues) since the
            // list query excludes content for bandwidth efficiency.
            const full = await getSwmsStoreItem(rec._id)
            setEditor({ open: true, record: full })
        } catch {
            showToast('Failed to load draft')
        }
    }

    const handleView = async (rec) => {
        try {
            const full = await getSwmsStoreItem(rec._id)
            setViewer({ open: true, record: full })
        } catch {
            showToast('Failed to load SWMS')
        }
    }

    const handleEditorSave = ({ title, content, fieldValues }) => {
        if (editor.record?._id) {
            updateMutation.mutate({ id: editor.record._id, data: { title, content, fieldValues } })
        } else {
            createMutation.mutate({ title, content, fieldValues, templateRef: editor.record?.templateRef || null })
        }
    }

    const handleConfirm = () => {
        if (!confirm) return
        const { type, record } = confirm
        if (type === 'delete') deleteMutation.mutate(record._id)
        if (type === 'publish') publishMutation.mutate(record._id)
        if (type === 'retire') retireMutation.mutate(record._id)
        setConfirm(null)
    }

    const toggleSection = (key) => setCollapsed((p) => ({ ...p, [key]: !p[key] }))

    const isLoading = loadingStore || loadingTemplates

    if (isLoading) return (
        <div className="sm-store-body" style={{ alignItems: 'center', padding: '48px 0' }}>
            <p style={{ color: 'var(--sm-text-tertiary)', fontSize: 13 }}>Loading…</p>
        </div>
    )

    return (
        <div className="sm-store-body">
            {/* Filter bar */}
            {availableTrades.length > 0 && (
                <div className="sm-filter-bar">
                    <p className="sm-filter-label">Filter by trade</p>
                    <div className="sm-filter-chips">
                        <button
                            className={`sm-chip${!tradeFilter ? ' active' : ''}`}
                            onClick={() => setTradeFilter(null)}
                        >
                            All
                        </button>
                        {availableTrades.map((t) => (
                            <button
                                key={t}
                                className={`sm-chip${tradeFilter === t ? ' active' : ''}`}
                                onClick={() => setTradeFilter(t)}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Templates */}
            <SectionBand
                sectionKey="templates" title="Templates" items={activeTemplates.length}
                collapsed={collapsed.templates} onToggle={() => toggleSection('templates')}
            >
                {activeTemplates.map((t) => (
                    <SwmsCard key={t._id} record={t} type="templates" onUseTemplate={handleUseTemplate} />
                ))}
                {activeTemplates.length === 0 && <p className="sm-empty">No active templates</p>}
            </SectionBand>

            {/* Drafts */}
            <SectionBand
                sectionKey="draft" title="Drafts" items={drafts.length}
                collapsed={collapsed.draft} onToggle={() => toggleSection('draft')}
            >
                {drafts.map((r) => (
                    <SwmsCard
                        key={r._id} record={r} type="draft"
                        onEdit={(rec) => handleEdit(rec)}
                        onCopy={(rec) => copyMutation.mutate(rec._id)}
                        onDelete={(rec) => setConfirm({ type: 'delete', record: rec, title: 'Delete Draft', message: 'Are you sure you want to delete this draft?' })}
                        onPublish={(rec) => setConfirm({ type: 'publish', record: rec, title: 'Publish SWMS', message: 'Once published this SWMS will be live and can no longer be edited.' })}
                    />
                ))}
                {drafts.length === 0 && <p className="sm-empty">No drafts</p>}
            </SectionBand>

            {/* Published */}
            <SectionBand
                sectionKey="published" title="Published" items={published.length}
                collapsed={collapsed.published} onToggle={() => toggleSection('published')}
            >
                {published.map((r) => (
                    <SwmsCard
                        key={r._id} record={r} type="published"
                        onView={handleView}
                        onCopy={(rec) => copyMutation.mutate(rec._id)}
                        onRetire={(rec) => setConfirm({ type: 'retire', record: rec, title: 'Retire SWMS', message: 'Retiring will remove this SWMS from active selection. Existing assignments are not affected.' })}
                    />
                ))}
                {published.length === 0 && <p className="sm-empty">No published SWMS</p>}
            </SectionBand>

            {/* Retired */}
            <SectionBand
                sectionKey="retired" title="Retired" items={retired.length}
                collapsed={collapsed.retired} onToggle={() => toggleSection('retired')}
            >
                {retired.map((r) => (
                    <SwmsCard
                        key={r._id} record={r} type="retired"
                        onView={handleView}
                    />
                ))}
                {retired.length === 0 && <p className="sm-empty">No retired SWMS</p>}
            </SectionBand>

            {/* Draft editor — key forces remount so Discard always resets state */}
            {editor.open && (
                <DraftEditor
                    key={editor.record?._id || 'new'}
                    initTitle={editor.record?.title}
                    initContent={editor.record?.content}
                    initFieldValues={editor.record?.fieldValues || {}}
                    onSave={handleEditorSave}
                    onClose={() => setEditor({ open: false, record: null })}
                    isSaving={createMutation.isLoading || updateMutation.isLoading}
                />
            )}

            {/* Read-only viewer */}
            <ViewerOverlay
                open={viewer.open}
                record={viewer.record}
                onClose={() => setViewer({ open: false, record: null })}
            />

            {/* Confirm modal */}
            <ConfirmModal confirm={confirm} onConfirm={handleConfirm} onCancel={() => setConfirm(null)} />

            {/* Toast */}
            <Toast message={toast.message} show={toast.show} isError={toast.isError} />
        </div>
    )
}
